import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import { useHandler } from "@redotech/react-util/hook";
import { showNotification } from "@redotech/redo-web/alert";
import { dismissNotificationByKey } from "@redotech/redo-web/notification-provider";
import { useEffect } from "react";
import { type EscalationContextType } from "./escalation-context";

const CODE_SEARCH_NOTIFICATION_KEY = "code-search-result";

interface UseCodeSearchNotificationParams {
  escalationContext: EscalationContextType;
  merchantRpcClient: RedoMerchantRpcClient;
  isOpen: boolean;
  open: () => void;
}

/**
 * Watches for code search results even when the chat panel is closed.
 * Shows a notification when the result arrives so the merchant can reopen.
 */
export function useCodeSearchNotification({
  escalationContext,
  merchantRpcClient,
  isOpen,
  open,
}: UseCodeSearchNotificationParams) {
  const pending = escalationContext.escalation.pendingCodeSearch;
  const noticeTopic = pending?.noticeTopic;
  const threadId = pending?.threadId;

  const onResult = useHandler(() => {
    if (!threadId) {
      return;
    }
    escalationContext.setEscalation({ pendingCodeSearch: null });
    // Belt-and-suspenders: the effect is torn down when the panel opens, but
    // a result yielding at the same instant could still race past the abort.
    if (isOpen) {
      return;
    }
    dismissNotificationByKey(CODE_SEARCH_NOTIFICATION_KEY);
    showNotification({
      notificationKey: CODE_SEARCH_NOTIFICATION_KEY,
      title: "We have an update on your question",
      description: "Our team looked into it and has a response for you.",
      primaryButton: {
        text: "Open chat",
        onClick: () => {
          escalationContext.navigateToThread?.(threadId);
          open();
        },
      },
    });
  });

  useEffect(() => {
    // While the panel is open, the in-chat handler owns the stream.
    if (isOpen || !noticeTopic || !threadId) {
      return;
    }

    const abort = new AbortController();

    void (async () => {
      try {
        for await (const event of merchantRpcClient.watchCodeSearchResult(
          { noticeTopic, triageThreadId: threadId },
          { signal: abort.signal },
        )) {
          if (event.type === "result-ready") {
            onResult();
            return;
          }
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        throw err;
      }
    })();

    return () => abort.abort();
  }, [isOpen, noticeTopic, threadId, merchantRpcClient, onResult]);
}
